import { useState } from 'react';
import { TextButton, PrimaryButton, WhiteButton } from '../Button/Buttons';
import { PencilFullIcon } from '../../../icons/SvgIcons';
import TextArea from '../TextArea/TextArea';

type DescriptionAndReasoningProps = {
  title?: string;
  content: string;
  isDescription: boolean;
  isEditable?: boolean;
  isDisabled?: boolean;
  onUpdate?: (newContent: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  alwaysVisible?: boolean;
};

export const DescriptionAndReasoning = ({
  title,
  content,
  isDescription,
  isEditable = true,
  isDisabled = false,
  onUpdate,
  onEditStart,
  onEditEnd,
  alwaysVisible = false
}: DescriptionAndReasoningProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftContent, setDraftContent] = useState(content);
  const [hoveredEditButton, setHoveredEditButton] = useState(false);

  const label = isDescription ? 'Description' : 'Reasoning';

  const handleToggle = (e?: React.MouseEvent<HTMLButtonElement>) => {
    setIsVisible((prev) => !prev);
    e?.currentTarget.blur();
  };

  const handleEditClick = () => {
    setDraftContent(content);
    setIsEditing(true);
    onEditStart?.();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraftContent(content);
    onEditEnd?.();
  };

  const hasChanged = draftContent !== content;

  const handleUpdate = () => {
    onUpdate?.(draftContent);
    setIsEditing(false);
    onEditEnd?.();
  };

  return (
    <>
      {!alwaysVisible && (
        <TextButton
          className="outline-none border-none shadow-none w-fit !text-[12px]"
          onClick={handleToggle}
          disabled={isDisabled}
        >
          {isVisible ? 'Hide' : 'Show'} {label}
        </TextButton>
      )}

      {(isVisible || alwaysVisible) && (
        <div className="relative flex flex-col gap-2 rounded-lg border border-gray-100 p-4">
          {isEditable && !isEditing && (
            <button
              className="p-1 outline-none border-none shadow-none w-fit absolute top-2 right-2 rounded-full hover:bg-gray-100"
              disabled={isDisabled}
              onClick={handleEditClick}
              onMouseOver={() => setHoveredEditButton(true)}
              onMouseOut={() => setHoveredEditButton(false)}
              title="Editing description may recalculate Risk Classification"
            >
              {PencilFullIcon(hoveredEditButton ? '#606169' : '#9CA3AF')}
            </button>
          )}

          {title && (
            <div className="border-b border-gray-100 pb-2 flex justify-between">
              <h3 className="font-base font-medium">{title}</h3>
            </div>
          )}

          <div className="flex flex-col gap-1 rounded-lg text-left">
            {isEditing ? null : (
              <h4 className="text-gray-550 text-[12px] font-medium uppercase">
                {label}:
              </h4>
            )}

            {isEditing ? (
              <>
                <TextArea
                  label={label + ' *'}
                  hint="Please provide a detailed description of your system’s intended purpose and context of use , as this information is the primary data source for risk classification and the generation of legal documentation. Specify if the system operates in critical areas like biometrics, education, employment, or infrastructure , or if it generates synthetic content. A comprehensive description is essential for an accurate analysis of your risk level and for defining your specific obligations."
                  value={draftContent}
                  onChange={setDraftContent}
                  rows={4}
                />
                <div className="flex gap-1 justify-end mt-1">
                  <WhiteButton size="xs" onClick={handleCancel}>
                    Cancel
                  </WhiteButton>
                  <PrimaryButton
                    size="xs"
                    onClick={handleUpdate}
                    disabled={!hasChanged || draftContent.trim() === ''}
                  >
                    Update
                  </PrimaryButton>
                </div>
              </>
            ) : (
              <div
                className="text-sm text-gray-900 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:pl-1"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
